import Plot from 'react-plotly.js';
import {Box, Paper, Typography, useTheme} from "@mui/material";
import {formatCurrency} from "../utils/formatters";

const BUBBLE_CHROME_H = 88;

const SEGMENT_COLORS = {
    'Champions':            '#2E7D32',
    'Loyal':                '#8BC34A',
    'Potential Loyalists':  'rgb(187, 214, 238)',
    'New Customers':        'rgb(68, 114, 196)',
    'Promising':            'rgb(236, 239, 241)',
    'Need Attention':       'rgb(255, 192, 0)',
    'At Risk':              '#F44336',
    'Hibernating':          '#FFEB3B',
    'Lost':                 '#9E9E9E'
};

export const RfmBubbleChart = ({customers, selectedSegment, cardHeight = null}) => {
    const theme = useTheme();
    const emptyPaperStyle = {
        ...theme.palette.contentPanel,
        padding: 20,
        height: cardHeight ?? 420,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box'
    };
    if (!customers || customers.length === 0) {
        return (
            <Paper style={emptyPaperStyle}>
                <Typography color="textSecondary">No customer data to plot</Typography>
            </Paper>
        );
    }

    const visible = selectedSegment
        ? customers.filter(c => c.segment === selectedSegment)
        : customers;

    // Bubble area scales with trailing-12m variable margin.
    const maxMarginAbs = Math.max(
        1,
        ...visible.map((customer) => Math.abs(customer.variable_margin || 0))
    );
    const sizeFor = (row) => {
        const margin = Math.abs(row.variable_margin || 0);
        const scaled = Math.sqrt(margin / maxMarginAbs) * 40;
        return Math.max(6, scaled);
    };

    // Group by segment so each shows up as its own trace in the legend.
    const segments = Array.from(new Set(visible.map(c => c.segment)));
    const traces = segments.map(segment => {
        const rows = visible.filter(c => c.segment === segment);
        return {
            name: segment,
            mode: 'markers',
            x: rows.map(r => r.days_since_last_order),
            y: rows.map(r => r.number_of_orders ?? 0),
            customdata: rows.map(r => ([
                r.name,
                r.segment,
                r.recency_score,
                r.frequency_score,
                r.days_since_last_order ?? 0,
                r.number_of_orders ?? 0,
                formatCurrency(r.net_revenue, true),
                formatCurrency(r.variable_margin || 0, true),
                formatCurrency(r.avg_order_value, true),
            ])),
            hovertemplate:
                '<b>%{customdata[0]}</b><br>' +
                'Segment: %{customdata[1]}<br>' +
                'R: %{customdata[2]} &nbsp; F: %{customdata[3]}<br>' +
                'Days since last order: %{customdata[4]}<br>' +
                'Number of orders: %{customdata[5]}<br>' +
                'Net Revenue: %{customdata[6]}<br>' +
                'Variable Margin: %{customdata[7]}<br>' +
                'Avg Order Value: %{customdata[8]}' +
                '<extra></extra>',
            marker: {
                size: rows.map(sizeFor),
                color: SEGMENT_COLORS[segment] || '#607D8B',
                line: {color: '#00000030', width: 1},
                sizemode: 'diameter'
            }
        };
    });

    const plotHeight = cardHeight != null
        ? Math.max(280, cardHeight - BUBBLE_CHROME_H)
        : 420;

    const paperStyle = {
        ...theme.palette.contentPanel,
        padding: 12,
        ...(cardHeight != null
            ? {
                height: cardHeight,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
                minHeight: 0
            }
            : {})
    };

    return (
        <Paper style={paperStyle}>
            <Typography variant="subtitle1" style={{fontWeight: 600, padding: '4px 8px'}}>
                RFM Cluster Map
            </Typography>
            <Typography variant="caption" color="textSecondary" style={{padding: '0 8px'}}>
                Bubble size = variable margin (12m) &nbsp;·&nbsp; X = days since last order &nbsp;·&nbsp; Y = orders (12m)
            </Typography>
            <Box sx={{flex: cardHeight != null ? 1 : undefined, minHeight: 0, width: '100%'}}>
                <Plot
                    data={traces}
                    layout={{
                        autosize: true,
                        height: plotHeight,
                        margin: {t: 20, b: 50, l: 50, r: 20},
                        xaxis: {title: {text: 'Days Since Last Order'}, rangemode: 'tozero'},
                        yaxis: {title: {text: 'Number of Orders'}, rangemode: 'tozero'},
                        legend: {orientation: 'v', x: 1.02, y: 1},
                        hovermode: 'closest'
                    }}
                    useResizeHandler
                    style={{width: '100%'}}
                    config={{displayModeBar: false}}
                />
            </Box>
        </Paper>
    );
};
